import WriteButton from "@/components/WriteButton";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Shield, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "cashier", active: true });

  const load = async () => {
    try { setUsers((await api.get("/users")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "cashier", active: true }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          name: form.name, role: form.role, active: form.active,
          ...(form.password ? { password: form.password } : {}),
        });
      } else {
        if (!form.email.trim() || !form.password) return toast.error("Email & password wajib diisi");
        await api.post("/users", { name: form.name, email: form.email.toLowerCase(), password: form.password, role: form.role });
      }
      toast.success("Pengguna disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus pengguna "${u.name}"?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Pengguna dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <PageHeader title="Pengguna" subtitle={`${users.length} akun`}>
        <WriteButton data-testid="add-user-btn" onClick={openNew} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
          <Plus className="h-4 w-4" /> Tambah Pengguna
        </WriteButton>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="rounded-xl border border-ui-line overflow-hidden" data-testid="user-auth-management">
          <table className="w-full text-sm">
            <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Nama</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Peran</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-ui-line hover:bg-ui-soft">
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-ui-muted">{u.email}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${u.role === "admin" ? "bg-accent-soft text-accent-readable" : "bg-brand-soft text-brand-readable"}`}>
                      {u.role === "admin" ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {u.role === "admin" ? "Admin" : u.role === "warehouse" ? "Gudang" : "Kasir"}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`text-xs ${u.active ? "text-brand-readable" : "text-ui-muted"}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <WriteButton plain data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-ui-muted-surface flex items-center justify-center"><Pencil className="h-4 w-4 text-ui-muted" /></WriteButton>
                      {u.id !== me.id && (
                        <WriteButton plain data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center"><Trash2 className="h-4 w-4 text-danger-readable" /></WriteButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Nama</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Email</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-ui-card border-ui-line disabled:opacity-60" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">{editing ? "Password baru (opsional)" : "Password"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-ui-card border-ui-line"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                  <SelectItem value="warehouse">Gudang</SelectItem><SelectItem value="cashier">Kasir</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center justify-between">
                <Label className="text-ui-muted text-sm">Akun Aktif</Label>
                <Switch data-testid="user-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
            <WriteButton data-testid="save-user-btn" onClick={save} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">Simpan</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
